<script setup>
import {
  insertSupplier,
  updateSupplier,
  querySupplierById,
} from "@/api/supplier";
import { reactive, ref } from "vue";
import { ElMessage } from "element-plus";
import { SUPPLIER_STATUS, ADJUST_TITLE } from "@/constants";
import { uploadFileMultiple } from "@/api/file";
import { postDownload } from "@/utils";

const emits = defineEmits(["afterSave"]);

// 弹窗控制
const visible = ref(false);

// 表单ref
const ruleRef = ref(null);

// 文件上传ref
const uploadRef = ref(null);

// 弹窗遮罩
const loading = ref(true);

// 文件列表
const fileList = ref([]);

// 表单对象
const formData = reactive({
  id: null,
  address: null,
  number: null,
  name: null,
  contact: null,
  mobile: null,
  status: null,
  startTime: null,
  endTime: null,
  remark: null,
  fileList: [],
});

// 描述列表
const descriptionColumns = ref([
  {
    label: "供应商编号",
    value: "number",
  },
  {
    label: "供应商名称",
    value: "name",
  },
  {
    label: "状态",
    value: "status",
  },
  {
    label: "合作开始时间",
    value: "startTime",
  },
  {
    label: "合作结束时间",
    value: "endTime",
  },
  {
    label: "联系人",
    value: "contact",
  },
  {
    label: "联系电话",
    value: "mobile",
  },
  {
    label: "地址",
    value: "address",
  },
  {
    label: "备注",
    value: "remark",
    span: 2,
  },
]);

// 表单校验
const rules = reactive({
  name: [{ required: true, message: "请输入供应商名称", trigger: "blur" }],
  number: [
    { required: true, message: "请输入供应商编号", trigger: "blur" },
    {
      pattern: /^[A-Za-z0-9]{1,}$/,
      message: "只能输入字母和数字",
      trigger: "blur",
    },
  ],
  contact: [{ required: true, message: "请输入联系人", trigger: "blur" }],
  mobile: [
    { required: true, message: "请输入联系方式", trigger: "blur" },
    {
      pattern: /^1[3|4|5|6|7|8|9][0-9]\d{8}$/,
      message: "请输入正确的电话",
      trigger: "blur",
    },
  ],
  status: [{ required: true, message: "请选择状态", trigger: "change" }],
  startTime: [{ required: true, message: "请选择开始时间", trigger: "change" }],
});

// 弹窗接收对象
const dialogConfig = ref({});

// 提交按钮防抖
const buttonLoading = ref(false);

// 新增编辑函数
const adjustFunc = reactive({
  add: insertSupplier,
  edit: updateSupplier,
});

// 校验时间
const disabledTime2 = (time) => {
  if (!formData.startTime) return;
  return Date.parse(formData.startTime.replace(/-/g, "/")) > time.getTime();
};

// 校验时间
const disabledTime1 = (time) => {
  if (!formData.endTime) return;
  return Date.parse(formData.endTime.replace(/-/g, "/")) < time.getTime();
};

// 弹窗打开
const handleDialogOpen = (config) => {
  loading.value = true;
  dialogConfig.value = { ...config };
  visible.value = true;
  ruleRef.value?.resetFields();
  if (!config.row) {
    Object.assign(formData, {
      id: null,
      address: null,
      number: null,
      name: null,
      contact: null,
      mobile: null,
      status: null,
      startTime: null,
      endTime: null,
      remark: null,
      fileList: [],
    });
    fileList.value = [];
    loading.value = false;
  } else {
    getInfoById(config.row?.id);
  }
};

// 通过id获取信息
const getInfoById = (id) => {
  querySupplierById(id)
    .then((res) => {
      const result = res.data;
      const keys = [
        "id",
        "name",
        "contact",
        "mobile",
        "startTime",
        "endTime",
        "address",
        "remark",
        "number",
        "fileList",
      ];

      keys.forEach((key) => {
        formData[key] = result[key];
      });
      fileList.value = result.fileList;
      fileList.value.map((i) => {
        i.name = i.fileName;
        i.uid = i.id;
      });
      if (dialogConfig.value.type == "detail") {
        formData.status = result.status.desc;
      } else {
        formData.status = result.status.value;
      }
      console.log(formData);
    })
    .finally(() => {
      loading.value = false;
    });
};

// 点击保存
const handleSubmit = () => {
  buttonLoading.value = true;
  ruleRef.value?.validate((valid) => {
    if (valid) {
      handleFileUpload();
    } else {
      buttonLoading.value = false;
    }
  });
};

// 执行保存
const execSubmit = () => {
  adjustFunc[dialogConfig.value.type]({ ...formData }).then((res) => {
    if (res.code == "2000") {
      ElMessage.success("操作成功");
      emits("afterSave");
      visible.value = false;
    }
  });
  setTimeout(() => {
    buttonLoading.value = false;
  }, 300);
};

// 文件变化
const onChange = (file, files) => {
  const allowSuffix = [".jpg", ".txt", ".pdf", ".xls", ".xlsx", ".png"];
  const suffix = file.name.substring(file.name.lastIndexOf("."));
  if (file.size / 1024 / 1024 > 5 || !allowSuffix.includes(suffix)) {
    ElMessage.error(`所选文件[${file.name}]格式或大小不正确，已过滤`);
    uploadRef.value?.handleRemove(file);
  }
};

// 文件超出限制
const onExceed = () => {
  ElMessage.error("最多上传三个文件");
};

// 移除文件
const removeFile = (file) => {
  uploadRef.value?.handleRemove(file);
};

// 执行文件上传
const handleFileUpload = async () => {
  if (fileList.value.length > 0) {
    const reqBody = new FormData();
    fileList.value.forEach((item) => {
      if (item.raw) {
        reqBody.append("files", item.raw);
      }
    });
    uploadFileMultiple(reqBody)
      .then((res) => {
        const result = res.data || [];
        result.forEach((item) => {
          formData.fileList.push({
            fileName: item.originalName,
            filePath: item.reduceUrl,
          });
        });
      })
      .finally(() => {
        execSubmit();
      });
  } else {
    execSubmit();
  }
};

// 点击文件下载
const fileDownload = (file) => {
  postDownload(
    "/atom/file/download",
    {
      downloadUrl: file.filePath,
    },
    file.fileName
  );
};

defineExpose({
  handleDialogOpen,
});
</script>

<template>
  <el-dialog
    :title="'供应商' + ADJUST_TITLE[dialogConfig.type]"
    :close-on-click-modal="false"
    width="50%"
    v-model="visible"
  >
    <el-descriptions
      border
      :column="2"
      v-if="dialogConfig.type == 'detail'"
      v-loading="loading"
    >
      <template v-for="item in descriptionColumns" :key="item.value">
        <el-descriptions-item
          :label="item.label"
          :span="item.span || 1"
          width="25%"
        >
          <span class="my_class">{{ formData[item.value] }}</span>
        </el-descriptions-item>
      </template>
      <el-descriptions-item label="附件" :span="2">
        <div class="file" v-for="item in fileList" :key="item">
          <span>{{ item.name }}</span>
          <span>
            <el-button
              type="primary"
              link
              icon="Download"
              class="ml5"
              @click="fileDownload(item)"
              >下载</el-button
            >
          </span>
        </div>
      </el-descriptions-item>
    </el-descriptions>

    <el-form
      :model="formData"
      ref="ruleRef"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
      v-else
    >
      <el-row :gutter="10">
        <el-col :span="12">
          <el-form-item label="供应商名称" prop="name">
            <el-input
              v-model="formData.name"
              placeholder="请输入供应商名称"
              :maxlength="20"
              v-no-space
            />
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="供应商编号" prop="number">
            <el-input
              v-model="formData.number"
              placeholder="请输入供应商编号"
              :maxlength="30"
              v-no-space
            />
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="状态" prop="status">
            <el-select
              v-model="formData.status"
              placeholder="请选择状态"
              style="width: 100%"
            >
              <el-option
                v-for="item in SUPPLIER_STATUS"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="合作开始时间" prop="startTime">
            <el-date-picker
              v-model="formData.startTime"
              type="date"
              style="width: 100%"
              placeholder="请选择合作开始时间"
              :disabled-date="disabledTime1"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="合作结束时间" prop="endTime">
            <el-date-picker
              v-model="formData.endTime"
              type="date"
              style="width: 100%"
              placeholder="请选择合作结束时间"
              :disabled-date="disabledTime2"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="联系人" prop="contact">
            <el-input
              v-model="formData.contact"
              placeholder="请输入联系人"
              :maxlength="20"
              v-no-space
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系电话" prop="mobile">
            <el-input
              v-no-space
              v-model="formData.mobile"
              placeholder="请输入联系方式"
              :maxlength="20"
            />
          </el-form-item>
        </el-col>

        <el-col :span="24">
          <el-form-item label="供应商地址" prop="address">
            <el-input
              v-model="formData.address"
              placeholder="请输入供应商地址"
              :maxlength="100"
            />
          </el-form-item>
        </el-col>

        <el-col :span="24">
          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="formData.remark"
              type="textarea"
              show-word-limit
              :maxlength="200"
              placeholder="请输入备注"
            />
          </el-form-item>
        </el-col>

        <el-col :span="24">
          <el-form-item label="附件">
            <el-upload
              ref="uploadRef"
              v-model:file-list="fileList"
              action=""
              multiple
              :limit="3"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="onChange"
              :on-exceed="onExceed"
              accept=".jpg,.txt,.pdf,.xls,.xlsx,.png"
            >
              <el-button type="primary">选择文件</el-button>
              <template #tip>
                <div class="el-upload__tip">
                  只能上传[.jpg,.txt,.pdf,.xls,.xlsx,.png]格式文件,
                  单个文件大小不可超过5MB
                </div>
                <div>
                  <div class="file" v-for="item in fileList" :key="item">
                    <span>{{ item.name }}</span>
                    <span>
                      <el-button
                        type="primary"
                        link
                        icon="Download"
                        class="ml5"
                        v-if="dialogConfig.type == 'edit' && item.filePath"
                        @click="fileDownload(item)"
                        >下载</el-button
                      >
                      <el-button
                        type="primary"
                        link
                        icon="Delete"
                        class="ml5"
                        @click="removeFile(item)"
                        >移除</el-button
                      >
                    </span>
                  </div>
                </div>
              </template>
            </el-upload>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleSubmit()"
          :disabled="buttonLoading"
          v-if="dialogConfig.type != 'detail'"
        >
          提交
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<style scoped>
.my_class {
  max-width: 100%;
  word-break: break-all;
  word-wrap: break-word;
}
.file {
  padding: 2px;
  display: flex;
  justify-content: space-between;
}
.file:hover {
  cursor: pointer;
  background: #f5f7fa;
}
.ml5 {
  margin-left: 5px;
}
</style>
